import React, { useCallback, useRef, useState } from "react";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRange } from "react-date-range";
import { TextField } from "@mui/material";

interface InstagramDateRangeProps {
    onDateChange: (startDate: string, endDate: string) => void;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

const formatDisplayDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const InstagramDateRange: React.FC<InstagramDateRangeProps> = ({ onDateChange }) => {
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date(new Date().setDate(new Date().getDate() - 6)),
            endDate: new Date(),
            key: "selection",
        },
    ]);

    // Always call onDateChange on mount and when dateRange changes
    React.useEffect(() => {
        const { startDate, endDate } = dateRange[0];
        onDateChange(formatDate(new Date(startDate)), formatDate(new Date(endDate)));
    }, [dateRange, onDateChange]);

    // Always call onDateChange on mount and when dateRange changes
    React.useEffect(() => {
        const { startDate, endDate } = dateRange[0];
        onDateChange(formatDate(new Date(startDate)), formatDate(new Date(endDate)));
    }, [dateRange, onDateChange]);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const calendarRef = useRef<HTMLDivElement | null>(null);

    const handleSelect = useCallback((ranges: any) => {
        const { startDate } = ranges.selection;
        const start = new Date(startDate);
        let end = new Date(start);
        end.setDate(start.getDate() + 6);
        const today = new Date();
        if (end > today) end = today;
        setWarningMessage(null);
        setDateRange([{ startDate: start, endDate: end, key: "selection" }]);
        onDateChange(formatDate(start), formatDate(end));
    }, [onDateChange]);

    const toggleCalendar = () => {
        setIsCalendarOpen((prev) => !prev);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
            setIsCalendarOpen(false);
        }
    };

    React.useEffect(() => {
        if (isCalendarOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isCalendarOpen]);

    return (
        <div style={{ position: "relative", display: "inline-block" }}>
            <TextField
                label="Select date"
                variant="outlined"
                fullWidth
                value={`${formatDisplayDate(dateRange[0].startDate)} to ${formatDisplayDate(dateRange[0].endDate)}`}
                onClick={toggleCalendar}
                InputProps={{
                    readOnly: true,
                    style: { cursor: 'pointer', background: '#fff' },
                }}
            />
            {isCalendarOpen && (
                <div ref={calendarRef} style={{
                    position: "absolute",
                    zIndex: 1000,
                    left: 0,
                    top: "100%",
                    minWidth: 320,
                    background: "white",
                    border: "1px solid #ccc",
                    padding: "10px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                }}>
                    <DateRange
                        editableDateInputs={true}
                        onChange={handleSelect}
                        moveRangeOnFirstSelection={false}
                        ranges={dateRange}
                        maxDate={new Date()}
                    />
                    {warningMessage && (
                        <div style={{ color: "red", marginTop: "10px" }}>{warningMessage}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default InstagramDateRange;
